import { useEffect } from 'react';
import { create } from '@bufbuild/protobuf';
import { App, Alert, Form, Input, Modal } from 'antd';
import MaasModelSelect from '@/components/MaasModelSelect';
import { useMaasCredentialModels } from '../hooks/useMaasModels';
import { useUpdateMaasCredential } from '../hooks/useMaasCredentials';
import { UpdateMaasCredentialRequestSchema } from '@/proto/agentplatform/httpv2/maas_pb';
import { type MaasCredential } from '@/proto/agentplatform/v2/maas_pb';
import { ApiError } from '@/api/errors';

interface Props {
  open: boolean;
  credential: MaasCredential | null;
  onClose: () => void;
}

interface FormValues {
  name: string;
  defaultModel: string;
}

export default function EditCredentialDialog({ open, credential, onClose }: Props) {
  const { message } = App.useApp();
  const [form] = Form.useForm<FormValues>();

  const { data: modelsRes, isLoading: loadingModels } = useMaasCredentialModels(
    open ? credential?.id : undefined
  );
  const updateMut = useUpdateMaasCredential();

  useEffect(() => {
    if (open && credential) {
      form.setFieldsValue({
        name: credential.name,
        defaultModel: credential.defaultModel,
      });
    }
  }, [open, credential, form]);

  async function onOk() {
    if (!credential) return;
    const values = await form.validateFields();
    try {
      await updateMut.mutateAsync(
        create(UpdateMaasCredentialRequestSchema, {
          id: credential.id,
          name: values.name,
          defaultModel: values.defaultModel,
        })
      );
      message.success('凭证已更新');
      onClose();
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : (e as Error).message);
    }
  }

  return (
    <Modal
      title="编辑模型凭证"
      open={open}
      onCancel={onClose}
      onOk={onOk}
      okText="保存"
      cancelText="取消"
      confirmLoading={updateMut.isPending}
      width={520}
      destroyOnClose
    >
      <Alert
        type="info"
        showIcon
        message="仅可修改凭证名称与默认模型，API Key 不可变更"
        style={{ marginBottom: 16 }}
      />
      <Form form={form} layout="vertical">
        <Form.Item
          label="凭证名称"
          name="name"
          rules={[{ required: true, message: '凭证名称必填' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label="默认模型"
          name="defaultModel"
          rules={[{ required: true, message: '默认模型必选' }]}
        >
          <MaasModelSelect
            models={modelsRes?.items ?? []}
            disabled={loadingModels}
            placeholder={loadingModels ? '加载模型列表...' : '请选择模型'}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
